import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Typography, Card, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, InputBase, CircularProgress, Button
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import useTheme from '../hooks/useTheme';
import ReportModal from './ReportModal';
import { fetchWithCache, fetchAllPages, performanceMonitor, debounce, apiCache } from '../utils/apiCache';

export default function Reports() {
  const theme = useTheme();
  const [reports, setReports] = useState([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);

  // Debounced search effect
  const debouncedSearchUpdate = useMemo(
    () => debounce((searchTerm) => {
      setDebouncedSearch(searchTerm);
    }, 300),
    []
  );

  useEffect(() => {
    debouncedSearchUpdate(search);
  }, [search, debouncedSearchUpdate]);

  // Fetch reports with pagination and performance monitoring
  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log('🚀 Starting paginated Reports fetch...');
        const data = await performanceMonitor.measureApiCall(
          'Reports/reports_with_users-paginated',
          fetchAllPages('https://api.uxhq.com/reports_with_users')
        );

        console.log(`📊 Reports: Loaded ${data.length} total reports`);

        // Ensure data is an array
        const reportsArray = Array.isArray(data) ? data : [];
        setReports(reportsArray);
      } catch (err) {
        console.error('Error fetching reports with users:', err);
        setError('Failed to load reports data. Please try again.');
        setReports([]);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  // Memoized filtering for better performance
  const filteredReports = useMemo(() => {
    if (!Array.isArray(reports)) return [];
    
    return reports.filter(report =>
      (report.name || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      (report.user_email || '').toLowerCase().includes(debouncedSearch.toLowerCase()) ||
      (report.role || '').toLowerCase().includes(debouncedSearch.toLowerCase())
    );
  }, [reports, debouncedSearch]);

  // Memoized event handlers to prevent unnecessary re-renders
  const handleViewChat = useCallback((report) => {
    const date = new Date(report.createdAt);
    const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
    const timeStr = date.toTimeString().split(' ')[0]; // HH:MM:SS
    
    setSelectedReport({
      userId: report.user_id,
      reportDate: dateStr,
      reportTime: timeStr
    });
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedReport(null);
  }, []);

  const handleRefresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Clear cache and fetch fresh paginated data
      console.log('🔄 Refreshing Reports with fresh paginated data...');
      apiCache.clearAll(); // Clear all cache for fresh fetch
      
      const data = await performanceMonitor.measureApiCall(
        'Reports/reports_with_users-refresh-paginated',
        fetchAllPages('https://api.uxhq.com/reports_with_users')
      );

      console.log(`📊 Reports Refresh: Loaded ${data.length} total reports`);

      const reportsArray = Array.isArray(data) ? data : [];
      setReports(reportsArray);
    } catch (err) {
      console.error('Error refreshing reports:', err);
      setError('Failed to refresh reports data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <Box sx={theme.styles.loadingContainer}>
        <CircularProgress size={40} sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ color: theme.textSecondaryColor }}>Loading reports...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={theme.styles.pageContainer}>
        <Typography variant="h4" sx={theme.styles.pageTitle}>
          REPORTS
        </Typography>
        <Card sx={{ ...theme.styles.card, p: 3, textAlign: 'center', mt: 3 }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
          <Button variant="contained" onClick={handleRefresh} sx={theme.styles.button}>
            Try Again
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={theme.styles.pageContainer}>
      {/* Page Title and Search Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={theme.styles.pageTitle}>
          REPORTS
        </Typography>
        <Box sx={theme.styles.searchBar}>
          <SearchIcon sx={{ color: theme.textSecondaryColor, mr: 1 }} />
          <InputBase
            placeholder="search by name or email"
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={theme.styles.searchInput}
          />
        </Box>
      </Box>

      {/* Reports Table */}
      <Card sx={theme.styles.card}>
        <TableContainer component={Paper} sx={{ ...theme.styles.card, borderRadius: 0 }}>
          <Table>
            <TableHead>
              <TableRow sx={theme.styles.tableHeader}>
                <TableCell sx={theme.styles.tableHeaderCell}>REPORT ID</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>EMAIL</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>NAME</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>ROLE</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>CREATED AT</TableCell>
                <TableCell sx={theme.styles.tableHeaderCell}>SHOW REPORTS</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReports.length > 0 ? filteredReports.map((report, index) => (
                <TableRow key={report.report_id || index} hover>
                  <TableCell sx={theme.styles.tableCell}>
                    {report.report_id || '-'}
                  </TableCell>
                  <TableCell sx={theme.styles.tableCell}>{report.user_email || '-'}</TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {report.name && report.name.length > 20 ? `${report.name.substring(0, 20)}...` : report.name || '-'}
                  </TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {report.role && report.role.length > 25 ? `${report.role.substring(0, 25)}...` : report.role || '-'}
                  </TableCell>
                  <TableCell sx={theme.styles.tableCell}>
                    {report.createdAt ? new Date(report.createdAt).toLocaleDateString('en-GB', { 
                      day: '2-digit', 
                      month: 'short', 
                      year: '2-digit' 
                    }) : '-'}
                  </TableCell>
                  <TableCell>
                    <Typography 
                      sx={theme.styles.link}
                      onClick={() => handleViewChat(report)}
                    >
                      View Report
                    </Typography>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', color: theme.textSecondaryColor, py: 4 }}>
                    No reports available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      
      {/* Report Modal */}
      <ReportModal
        open={modalOpen}
        onClose={handleCloseModal}
        userId={selectedReport?.userId}
        reportDate={selectedReport?.reportDate}
        reportTime={selectedReport?.reportTime}
      />
    </Box>
  );
} 